// sketch_classifier_web.js - ONNX model inference for web browsers
// Requires: onnxruntime-web (npm install onnxruntime-web)

import * as ort from 'onnxruntime-web';
class WebSketchClassifier {
    constructor() {
        this.session = null;
        this.classLabels = [
            'ant', 'apple', 'arm', 'axe', 'banana', 'basket', 'bear', 'bed', 'bee', 'bench', 
            'bicycle', 'bird', 'book', 'bread', 'bridge', 'bus', 'butterfly', 'cactus', 'cake', 
            'calculator', 'camel', 'candle', 'car', 'carrot', 'castle', 'cat', 'chair', 'church', 
            'cloud', 'cow', 'crab', 'crocodile', 'crown', 'cup', 'dog', 'door', 'ear', 'elephant', 
            'eye', 'face', 'fan', 'feather', 'fish', 'foot', 'frog', 'giraffe', 'hat', 'helicopter', 
            'helmet', 'horse', 'house', 'kangaroo', 'knife', 'ladder', 'leaf', 'lion', 'lobster', 
            'monkey', 'moon', 'mosquito', 'mouth', 'mug', 'nose', 'owl', 'parrot', 'pig', 'pineapple', 
            'pizza', 'rabbit', 'radio', 'scissors', 'screwdriver', 'sheep', 'shoe', 'snail', 'snake', 
            'spider', 'spoon', 'squirrel', 'sun', 'swan', 'syringe', 'telephone', 'television', 'tent', 
            'tiger', 'toilet', 'tooth', 'toothbrush', 'tractor', 'train', 'tree', 'truck', 'trumpet', 
            'umbrella', 'van', 'violin', 'wheel', 'windmill', 'zebra'
        ];
    }

	async loadModelFromBlob(modelBlob, dataBlob) {
		try {
            if (!dataBlob) {
                throw new Error("Missing external ONNX data blob (model .data file)");
            }

			const modelBuffer = await modelBlob.arrayBuffer();

			this.session = await ort.InferenceSession.create(modelBuffer, {
				executionProviders: ["webgpu", "wasm"],
				externalData: [
					{
                        path: "model.onnx.data",
                        data: dataBlob,
                    },
                    {
                        path: "mobilenet_sketch_classifier.onnx.data",
						data: dataBlob,
					},
				],
			});

			console.log("Split model loaded successfully!");
			return this.session;
		} catch (error) {
			console.error("Error loading split model:", error);
			throw error;
		}
	}

	async preprocessImageFromBlob(imageBlob) {
		return new Promise((resolve, reject) => {
			const img = new Image();
			const url = URL.createObjectURL(imageBlob);

			img.onload = () => {
				try {
					const TARGET = 224;
					const PAD = 10;

					// Pad with white, equivalent to transforms.Pad(padding=10, fill=255)
					const paddedCanvas = document.createElement('canvas');
					paddedCanvas.width = img.width + 2 * PAD;
					paddedCanvas.height = img.height + 2 * PAD;

					const paddedCtx = paddedCanvas.getContext('2d', { willReadFrequently: true });
					paddedCtx.fillStyle = 'rgb(255, 255, 255)';
					paddedCtx.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);
					paddedCtx.drawImage(img, PAD, PAD);

					// Resize to 224x224, equivalent to transforms.Resize((224, 224))
					const canvas = document.createElement('canvas');
					canvas.width = TARGET;
					canvas.height = TARGET;

					const ctx = canvas.getContext('2d', { willReadFrequently: true });
					ctx.imageSmoothingEnabled = true;
					ctx.imageSmoothingQuality = 'high';
					ctx.drawImage(paddedCanvas, 0, 0, TARGET, TARGET);

					// Read resized pixel data
					const imageData = ctx.getImageData(0, 0, TARGET, TARGET);
					const data = imageData.data;

					// Build CHW float tensor: [3, 224, 224]
					const inputArray = new Float32Array(3 * TARGET * TARGET);
					const planeSize = TARGET * TARGET;

					let idx = 0;
					for (let i = 0; i < data.length; i += 4) {
						const r = data[i];
						const g = data[i + 1];
						const b = data[i + 2];

						// Equivalent to transforms.Grayscale(num_output_channels=3)
						const gray255 = 0.2989 * r + 0.5870 * g + 0.1140 * b;

						// Equivalent to ToTensor() + Normalize(mean=[0.5]*3, std=[0.5]*3)
						const gray01 = gray255 / 255.0;
						const normalized = (gray01 - 0.5) / 0.5;

						inputArray[idx] = normalized;
						inputArray[idx + planeSize] = normalized;
						inputArray[idx + 2 * planeSize] = normalized;
						idx++;
					}

					URL.revokeObjectURL(url);
					resolve(inputArray);
				} catch (error) {
					URL.revokeObjectURL(url);
					reject(error);
				}
			};

			img.onerror = () => {
				URL.revokeObjectURL(url);
				reject(new Error('Failed to load image from blob'));
			};

			img.src = url;
		});
	}


    async predict(imageBlob, topK = 10) {
        if (!this.session) {
            throw new Error('Model not loaded. Call loadModelFromBlob() first.');
        }

        try {
            // Preprocess the image
            const inputTensor = await this.preprocessImageFromBlob(imageBlob);
            
            // Create input tensor for ONNX
            const tensor = new ort.Tensor('float32', inputTensor, [1, 3, 224, 224]);
            
            // Run inference
            const feeds = { [this.session.inputNames[0]]: tensor };
            const results = await this.session.run(feeds);
            
            // Get output tensor
            const output = results[this.session.outputNames[0]];
            const predictions = Array.from(output.data);
            
            // Get top-K predictions
            const indexed = predictions.map((score, index) => ({ score, index }));
            indexed.sort((a, b) => b.score - a.score);
            
            const topPredictions = indexed.slice(0, topK).map(item => ({
                label: this.classLabels[item.index],
                confidence: this.softmax(predictions)[item.index],
                score: item.score
            }));
            
            return topPredictions;
            
        } catch (error) {
            console.error('Error during prediction:', error);
            throw error;
        }
    }

    async predictWithComparison(imageBlob, baseWord, topK = 5) {
        const predictions = await this.predict(imageBlob, topK);
        
        // Find if baseWord matches any prediction
        const baseWordLower = baseWord.toLowerCase();
        const matchingPrediction = predictions.find(pred => 
            pred.label.toLowerCase() === baseWordLower
        );
        
        const result = {
            predictions: predictions,
            baseWord: baseWord,
            isMatch: !!matchingPrediction,
            matchConfidence: matchingPrediction ? matchingPrediction.confidence : 0,
            topPrediction: predictions[0]
        };
        
        return result;
    }

    softmax(arr) {
        const max = Math.max(...arr);
        const exps = arr.map(x => Math.exp(x - max));
        const sum = exps.reduce((a, b) => a + b, 0);
        return exps.map(x => x / sum);
    }

    getSupportedLabels() {
        return [...this.classLabels];
    }
}

// Exported function for use in other files
async function classifySketch(modelBlob, dataBlob, imageBlob, baseWord, options = {}) {
    const {
        topK = 10,
        returnComparison = true
    } = options;
    
    try {
        const classifier = new WebSketchClassifier();
        await classifier.loadModelFromBlob(modelBlob, dataBlob);
        
        if (baseWord && returnComparison) {
            return await classifier.predictWithComparison(imageBlob, baseWord, topK);
        } else {
            const predictions = await classifier.predict(imageBlob, topK);
            return {
                predictions: predictions,
                topPrediction: predictions[0]
            };
        }
    } catch (error) {
        console.error('Error in classifySketch:', error);
        throw error;
    }
}
// ES Module exports (for import/export syntax)
export { WebSketchClassifier, classifySketch};

// Default export
export default classifySketch;